// Presentational HUD components. All state lives in the Viewer controller / App; styles
// are CSS classes in index.html ("cyberpunk business-casual" theme tokens).

import { createSignal, For, Show } from "solid-js";
import type { Scene, Viewer } from "./viewer.ts";

export function Hud(props: { viewer: Viewer; scenes: Scene[]; scenesError: string | null }) {
  const v = props.viewer;
  return (
    <div class="hud-panel">
      <div class="hud-title">
        <span
          class="hud-dot"
          style={{ background: v.supported() ? "var(--accent)" : "var(--accent-2)" }}
        />
        <span class="hud-brand">GSPLAT</span>
      </div>

      <div style={{ display: "grid", "row-gap": "8px" }}>
        <div>
          <div class="hud-label">status</div>
          <div class="hud-value" style={{ "font-size": "12px", color: "var(--text)" }}>
            {v.status()}
          </div>
        </div>

        <Show when={v.supported()}>
          <div style={{ display: "flex", gap: "20px" }}>
            <div>
              <div class="hud-label">fps</div>
              <div class="hud-value">{v.fps()}</div>
            </div>
            <div>
              <div class="hud-label">splats</div>
              <div class="hud-value">{v.splats().toLocaleString()}</div>
            </div>
          </div>

          <label class="hud-btn load-btn">
            load .ply
            <input
              type="file"
              accept=".ply"
              style={{ display: "none" }}
              onChange={(e) => {
                const f = e.currentTarget.files?.[0];
                if (f) v.loadFile(f);
              }}
            />
          </label>

          <SceneList viewer={v} scenes={props.scenes} error={props.scenesError} />
          <SortControls viewer={v} />
        </Show>

        <Show when={!v.supported()}>
          <div class="hud-note">
            Use a Chromium-based browser (Chrome/Edge) or Safari 18+ with WebGPU enabled.
          </div>
        </Show>
      </div>
    </div>
  );
}

function SceneList(props: { viewer: Viewer; scenes: Scene[]; error: string | null }) {
  return (
    <div style={{ "margin-top": "6px", display: "grid", "row-gap": "6px" }}>
      <div class="hud-label">scenes</div>
      <Show
        when={!props.error}
        fallback={<div class="hud-mono-note" style={{ color: "var(--accent-2)" }}>{props.error}</div>}
      >
        <Show
          when={props.scenes.length > 0}
          fallback={<div class="hud-mono-note">no server scenes — drop a .ply in data/</div>}
        >
          <div style={{ display: "grid", "row-gap": "4px" }}>
            <For each={props.scenes}>
              {(scene) => (
                <button
                  type="button"
                  classList={{
                    "scene-btn": true,
                    active: props.viewer.activeScene() === scene.file,
                  }}
                  onClick={() => props.viewer.loadScene(scene)}
                >
                  {scene.name}
                </button>
              )}
            </For>
          </div>
        </Show>
      </Show>
    </div>
  );
}

function SortControls(props: { viewer: Viewer }) {
  // (The bitonic "gpu" path was retired — cpu is the oracle, radix is production.)
  const [sortMode, setSortMode] = createSignal<"cpu" | "radix">("radix");
  const [validate, setValidate] = createSignal(false);
  const choose = (m: "cpu" | "radix") => {
    setSortMode(m);
    props.viewer.setSortMode(m);
  };
  return (
    <div style={{ "margin-top": "8px", display: "grid", "row-gap": "6px" }}>
      <div class="hud-label">sort</div>
      <div style={{ display: "flex", gap: "4px" }}>
        <button
          type="button"
          classList={{ "sort-btn": true, active: sortMode() === "cpu" }}
          onClick={() => choose("cpu")}
        >
          cpu
        </button>
        <button
          type="button"
          classList={{ "sort-btn": true, active: sortMode() === "radix" }}
          onClick={() => choose("radix")}
        >
          radix
        </button>
      </div>
      <button
        type="button"
        classList={{ "validate-btn": true, active: validate() }}
        onClick={() => {
          const on = !validate();
          setValidate(on);
          props.viewer.setValidate(on);
        }}
      >
        <span classList={{ "validate-dot": true, active: validate() }} />
        validate (console)
      </button>
    </div>
  );
}
